import axios from 'axios'

export const API_BASE_URL = 'https://diamondaccountingback.liara.run' // Your FastAPI backend
//export const API_BASE_URL = 'http://localhost:8000' // For local development

export const API_MESSENGER_URL = 'https://diamondmessenger.liara.run'
//export const API_MESSENGER_URL = 'http://localhost:9000'

export const API_MAP_URL = 'https://api.neshan.org/v4'
export const NESHAN_API_KEY = 'service.2cce2a429f0c48fb8bd0decd4e9c1235';

// ============= ABORT CONTROLLER MANAGEMENT =============
const activeRequests = new Map();

export const createAbortController = (requestId) => {
  const controller = new AbortController();
  if (requestId) {
    activeRequests.set(requestId, controller);
  }
  return controller;
};

export const cancelRequest = (requestId) => {
  const controller = activeRequests.get(requestId);
  if (controller) {
    controller.abort();
    activeRequests.delete(requestId);
    console.log(`Request ${requestId} cancelled`);
    return true;
  }
  return false;
};

export const cancelAllRequests = () => {
  console.log(`Cancelling ${activeRequests.size} active requests...`);
  activeRequests.forEach((controller, requestId) => {
    controller.abort();
    console.log(`Cancelled request: ${requestId}`);
  });
  activeRequests.clear();
  console.log('All requests cancelled');
};

export const getActiveRequestCount = () => {
  return activeRequests.size;
};

// Generate unique request ID
export const generateRequestId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000,
})

// Request interceptor to add auth token and abort controller
const authInterceptor = (config) => {
  const token = localStorage.getItem('token')
  const preAuthToken = localStorage.getItem('preAuthToken')
  
  console.log('API Request to:', config.url)
  
  // Add abort controller if not already present
  if (!config.signal) {
    const requestId = config.requestId || generateRequestId();
    config.requestId = requestId;
    const controller = createAbortController(requestId);
    config.signal = controller.signal;
  }
  
  // Add auth header
  if ((config.url.includes('/select-company') && preAuthToken) || (config.url.includes('/company/create') && preAuthToken)) {
    config.headers.Authorization = `Bearer ${preAuthToken}`
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Only set default Content-Type for non-multipart requests
  if (!config.headers['Content-Type']) {
    if (config.url.includes('/company/create')) {
      config.headers['Content-Type'] = 'multipart/form-data'
    } else {
      config.headers['Content-Type'] = 'application/json'
    }
  }
  
  return config
}

api.interceptors.request.use(authInterceptor)

// Response interceptor to handle errors and cleanup
const errorInterceptor = (error) => {
  // Check if request was cancelled
  if (axios.isCancel(error)) {
    console.log('Request cancelled:', error.message)
    return Promise.reject({ 
      cancelled: true, 
      message: 'Request was cancelled by user',
      status: 499
    })
  }
  
  console.error('API Error:', error)
  
  if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
    console.error('Backend is not running or not accessible')
  }
  
  if (error.response?.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('companies')
    localStorage.removeItem('selectedCompany')
    window.location.href = '/login'
  }
  
  // Cleanup abort controller
  if (error.config?.requestId) {
    activeRequests.delete(error.config.requestId)
  }
  
  return Promise.reject(error)
}

// Response interceptor for success
const successInterceptor = (response) => {
  // Cleanup abort controller
  if (response.config?.requestId) {
    activeRequests.delete(response.config.requestId)
  }
  
  console.log('✅ API Response:', {
    status: response.status,
    data: response.data,
    url: response.config.url
  })
  return response
}

api.interceptors.response.use(successInterceptor, errorInterceptor)

export const apiWithAbort = {
  get: (url, params = {}, requestId = null) => {
    const config = {
      params,
      requestId: requestId || generateRequestId()
    };
    return api.get(url, config);
  },
  
  post: (url, data = {}, requestId = null) => {
    const config = {
      requestId: requestId || generateRequestId()
    };
    return api.post(url, data, config);
  },
  
  put: (url, data = {}, requestId = null) => {
    const config = {
      requestId: requestId || generateRequestId()
    };
    return api.put(url, data, config);
  },
  
  delete: (url, requestId = null) => {
    const config = {
      requestId: requestId || generateRequestId()
    };
    return api.delete(url, config);
  }
};

export const authService = {
  login: (phoneNumber, password) =>
    api.post('/login', { 
      phone_number: phoneNumber, 
      password: password 
    }),

  selectCompany: (companyId, selectedSubcode = null) =>
    api.post('/select-company', { 
      company_id: companyId,
      ...(selectedSubcode !== null ? { selected_subcode: selectedSubcode } : {})
    }),

  selectStoreWithToken: (storeId, token) =>
    api.post('/select-store', 
      { company_id: storeId },
      { headers: { Authorization: `Bearer ${token}` } }
  ),

  logout: () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('companies')
    localStorage.removeItem('selectedCompany')
    return Promise.resolve()
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user')
    return Promise.resolve(user ? JSON.parse(user) : null)
  },

  resetPassword: (phoneNumber, otpCode, newPassword) =>
    api.post('/reset-password', {
      phone_number: phoneNumber,
      otp_code: otpCode,
      new_password: newPassword
    }),

  // New method to check if token has company info
  hasCompanySelected: () => {
    const token = localStorage.getItem('token')
    if (!token) return false
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return !!(payload.company_db && payload.company_id)
    } catch {
      return false
    }
  },

  registerCompany: (companyId, otpCode) =>
    api.post('/register-company', { 
      company_id: companyId,
      otp_code: otpCode
  }),

  selectCompanyWithToken: (companyId, preAuthToken) =>
    api.post('/select-company', 
      { company_id: companyId },
      { headers: { Authorization: `Bearer ${preAuthToken}` } }
    ),

  registerCustomer: (customerData) =>
    api.post('/sales/register-customer', customerData),

  getCustomerRegistrationStatus: () =>
    api.get('/customer/registration-status'),

  completeCustomerRegistration: (customerData) =>
    api.post('/customer/register-customer', customerData),
}

export const companyService = {
  createCompany: (formData) =>
    api.post('/company/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),

  // Public endpoint - no auth required
  getPublicCompanyDetails: (companyIdentifier) => 
    api.get(`/company/public/${companyIdentifier}`),
  
  // Get company by name (for routing)
  getCompanyByName: (companyName) =>
    api.get(`/company/by-name/${encodeURIComponent(companyName)}`),

  // Get all companies for current user (if needed separately)
  getUserCompanies: () => {
    const companies = localStorage.getItem('companies')
    return Promise.resolve(companies ? JSON.parse(companies) : [])
  },

  setSelectedCompany: (company) => {
    localStorage.setItem('selectedCompany', JSON.stringify(company))
    return Promise.resolve()
  },

  getSelectedCompany: () => {
    const company = localStorage.getItem('selectedCompany')
    return Promise.resolve(company ? JSON.parse(company) : null)
  }
}

export const profileService = {
  // Get customer profile from backend using token
  getCustomerProfile: () =>
    api.get('/customer/profile'),

  // Update customer profile
  updateProfile: (customerData) =>
    api.put('/update-customer', customerData),

  // Legacy methods (keep for backward compatibility)
  getCustomerByPhone: (phoneNumber) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    if (user.phone === phoneNumber) {
      return Promise.resolve(user)
    }
    return Promise.reject(new Error('Customer not found'))
  },

  getProfile: (customerId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    if (user.id === customerId) {
      return Promise.resolve(user)
    }
    return Promise.reject(new Error('Profile not found'))
  }
}

export const registerService = {
  // Send OTP - matches your FastAPI /send-otp endpoint
  register: (phoneNumber) =>
    api.post('/send-otp', { 
      phone_number: phoneNumber 
    }),

  // Verify OTP - matches your FastAPI /verify-otp endpoint
  verifyOtp: (phoneNumber, otp) =>
    api.post('/verify-otp', { 
      phone_number: phoneNumber, 
      otp_code: otp 
    }),

  // Set password - matches your FastAPI /set-password endpoint
  setPassword: (phoneNumber, password) =>
    api.post('/set-password', {
      phone_number: phoneNumber,
      password: password
    }),

  // Resend OTP - same as register/send-otp
  resendOtp: (phoneNumber) =>
    api.post('/send-otp', { 
      phone_number: phoneNumber 
    })
}

export const accountingService = {
  getAccountingData: (vahedPol = 1, sortBy = "Dates", sortOrder = "desc") =>
    api.get(`/accounting-data-with-fields?vahed_pol=${vahedPol}&sort_by=${sortBy}&sort_order=${sortOrder}`),

  getRemainingCash: (arzId = 1) =>
    api.get(`/remaining-cash?arz_id=${arzId}`),

  getCommercialDetails: (invoiceAuto, atf, ac, actCode) =>
    api.get(`/commercial-details-pivot/${invoiceAuto}`, 
    {
      params: { atf: atf, ac: ac, actCode: actCode }
    }
    ),

  getAllArz: () =>
    api.get('/get-all-arz'),

  getTaahodatO: (ac) =>
    api.post('/taahodatO', { ac: ac }),
  
  getTaahodatY: (ac) =>
    api.post('/taahodatY', { ac: ac }),

  getKalaO: (ac) =>
    api.post('/kalaO', { ac: ac }),

  getKalaY: (ac) =>
    api.post('/kalaY', { ac: ac })
}

export const employerService = {
  getTarazData: (tarazRequest) =>
    api.post('/employer/traz', tarazRequest),

  getTarazWithAnalysis: (combinedRequest, customTimeout = 300000) => // 5 minutes default
    api.post('/employer/traz-with-analysis', combinedRequest, {
      timeout: customTimeout  // Override the default 100000ms
    }),

  getTodayTaraz: (payload) =>
   api.post('/employer/today-traz', payload),

  getCurrenciesPrice: () =>
    api.post('/employer/currency-price'),

  searchCustomers: (searchTerm) =>
    api.post('/employer/search-customer', { search_term: searchTerm }),

  getAccountingData: (vahed_pol, id_subcode, sort_order = 'desc', sort_by = 'Dates') =>
  api.get(`/employer/accounting-data-with-fields/` + id_subcode, {
    params: { 
      vahed_pol,
      sort_order,
      sort_by
    }
  }),

  getMandeReport: (reportRequest) =>
    api.post('/employer/report-mande', reportRequest),

  getMandeReport2: (reportRequest) =>
    api.post('/employer/report-mande2', reportRequest),
  
  getTaseerSood: (reportRequest) =>
    api.post('/employer/taseer-sood', reportRequest),

  getRemainingCash: (arz, subCode) =>
    api.get('/employer/customer/remaining-cash', {
      params: { arz_id: arz, subcode_id: subCode }
    }),

  getCustomerProfile: (subCode) =>
    api.get('/employer/customer/get-profile', {
      params: { subcode_id: subCode }
    }),
  
  getDailyActivityReport: (reportDate)  =>
    api.get(`/employer/daily-activity-report`, {
      params: {
        report_date: reportDate
      }
    }),

  getDailyDetailReport: (act, reportDate) =>
    api.get(`/employer/daily-detail-report`, {
      params: { 
        act: act,
        report_date: reportDate 
      }
    }),

  getDailySaleReport: (reportDate)  =>
    api.get(`/employer/daily-sale-report`, {
      params: {
        report_date: reportDate
      }
    }),

  getLiquidityRatios: () =>
    api.get('/employer/liquidity-ratios'),

  getFactorDetails: (invoiceAuto, ac, actCode) =>
    api.get(`/employer/factor-detail/${invoiceAuto}`, 
    {
      params: {ac: ac, actCode: actCode }
    }
    ),

  getActs: () =>
    api.get('/employer/acts'),

  getMonthlySood: () =>
    api.get('/employer/monthly-sood'),
  
  getMonthlyActivityReport: (act) =>
    api.get('/employer/monthly-activity-report', {
      params: { act }
    }),

  getTarazSood: (arz = 1, cmLevel = 3) =>
      api.get('/employer/taraz-sood', {
          params: { arz, cm_level: cmLevel }
      }),

  analyzeFinancialRatios: (data) =>
      api.post('/employer/analyze-financial-ratios', data, {
      timeout: 300000  // Override the default 100000ms
    }),

  // ============= NEW OPERATOR MANAGEMENT METHODS =============
  
  // Operator Categories
  getOperatorCategories: () =>
    api.get('/employer/operator/categories'),

  createOperatorCategory: (categoryData) =>
    api.post('/employer/operator/categories', categoryData),

  updateOperatorCategory: (categoryId, categoryData) =>
    api.put(`/employer/operator/categories/${categoryId}`, categoryData),

  deleteOperatorCategory: (categoryId) =>
    api.delete(`/employer/operator/categories/${categoryId}`),

  // Company Operators
  getCompanyOperators: (filters = {}) =>
    api.get('/employer/operators', { params: filters }),

  registerCompanyOperator: (operatorData) =>
    api.post('/employer/operator/register', operatorData),

  updateCompanyOperator: (operatorId, operatorData) =>
    api.put(`/employer/operator/${operatorId}`, operatorData),

  deleteCompanyOperator: (operatorId, permanent = false) =>
    api.delete(`/employer/operator/${operatorId}`, { params: { permanent } }),

  syncOperatorWithMessenger: (operatorId) =>
    api.post(`/employer/operator/sync-with-messenger/${operatorId}`),

  searchCustomerForOperator: (searchRequest) =>
    api.post('/employer/operator/search-customer-for-operator', searchRequest),

  registerOperatorFromCustomer: (registrationData) =>
    api.post('/employer/operator/register-from-customer', registrationData),

  // Operator Stats
  getOperatorStats: () =>
    api.get('/employer/operator/stats'),

  getActiveChats: () =>
    api.get('/employer/operator/active-chats'),

  getPendingQuestions: (department = null) =>
    api.get('/employer/operator/pending-questions', { 
      params: department ? { department } : {} 
    }),

  // Webhook (usually not called from frontend)
  // This is for backend-to-backend communication
}

export const SalesService = {
  getStoreLayout: () => api.get('/company/store-layout'),
  getKalaList: async (page = 1, pageSize = 20, searchQuery = '') => {
    return api.post('/sales/kala-list', null, {
      params: {
        page,
        page_size: pageSize,
        search_query: searchQuery
      }
    })
  },
  
  // Search kala with filters
  searchKala: async (searchRequest) => {
    return api.post('/sales/kala/search', searchRequest)
  },

  searchKala2: (searchRequest) =>
    api.post('/sales/kala/search2', searchRequest),
  
  // Get top selling kala
  getTopSellingKala: async (limit = 10) => {
    return api.get('/sales/kala/top-selling', {
      params: { limit }
    })
  },

  getKalaByCategory: async (categoryId, page = 1, pageSize = 20, searchQuery = '') => {
    // Ensure categoryId is a primitive value
    const cleanCategoryId = typeof categoryId === 'object' 
      ? (categoryId.category_id || categoryId.id || String(categoryId)) 
      : String(categoryId)
    
    console.log('Sending category_id:', cleanCategoryId)
    console.log('Sending search_query:', searchQuery)
    
    // Create params object
    const params = {
      category_id: cleanCategoryId,
      page: Number(page),
      page_size: Number(pageSize)
    }
    
    // Only add search_query if it has a value
    if (searchQuery && searchQuery.trim()) {
      params.search_query = searchQuery.trim()
    }
    
    console.log('Request params:', params)
    
    return api.post('/sales/kala/by-category', null, { params })
  },

  getCategoryProductCounts: async () => {
    return api.get('/sales/category-product-counts')
  },

  saveInvoice: async (invoiceData) => {
    return api.post('/sales/save-invoice', invoiceData)
  },

  saveCheck: async (checkData) => api.post('/sales/save-check', checkData),

  selectCommodity2: (typ, page, pageSize = 20) =>
    api.post('/sales/kala/select-commodity2', null, {
      params: { typ, page, page_size: pageSize }
  }),
  selectCommodity3: (typ, page) =>
    api.get('/sales/kala/select-commodity3', { params: { typ, page } }),
  getInvoiceDetail: (invoiceId, ac, act) =>
      api.get(`/sales/invoice/${invoiceId}`, {
          params: { ac, act }
      }),
  
  okInvoice: (invoiceId) =>
      api.post('/sales/invoice/ok-invoice', null, {
          params: { invoice_id: invoiceId }
      }),

  getDepotCardPrice: (commodityCode) =>
    api.get('/employer/depot-card-price', {
      params: { commodity_code: commodityCode }
    }),
}

export const operatorService = {
  checkIfOperator: () =>
    api.get('/customer/is-operator'),
}

export const MapService = {
  searchLocation: async (term, lat, lng) => {
    const apiKey = NESHAN_API_KEY
    const url = `https://api.neshan.org/v1/search?term=${encodeURIComponent(term)}&lat=${lat}&lng=${lng}`
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Api-Key': apiKey,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('MapService search error:', error)
      throw error
    }
  }
}

export const peakService = {
  // Register as a driver
  registerDriver: (driverData) =>
    api.post('/peak/driver/register', driverData),

  // Get driver profile
  getDriverProfile: () =>
    api.get('/peak/driver/profile'),

  // Update driver profile
  updateDriverProfile: (driverData) =>
    api.put('/peak/driver/update', driverData),

  // Check if customer is already a driver
  checkIsDriver: () =>
    api.get('/peak/driver/check'),

  // Add new vehicle to existing driver
  addVehicle: (vehicleData) =>
    api.post('/peak/driver/vehicles', vehicleData),

  // Remove vehicle
  removeVehicle: (vehicleId) =>
    api.delete(`/peak/driver/vehicles/${vehicleId}`),

  // Update vehicle
  updateVehicle: (vehicleId, vehicleData) =>
    api.put(`/peak/driver/vehicles/${vehicleId}`, vehicleData),

  // Get all vehicles for driver
  getVehicles: () =>
    api.get('/peak/driver/vehicles')
}

export const deliveryService = {
  // Create a new delivery
  createDelivery: (deliveryData) =>
      api.post('/delivery/create', deliveryData),
  
  getPendingDeliveries: (driverId, lat, lng, radiusKm = 10) => {
      // Ensure driverId is a number
      const driverIdNum = parseInt(driverId)
      if (isNaN(driverIdNum)) {
          throw new Error(`Invalid driver ID: ${driverId}`)
      }
      
      return api.get(`/delivery/pending/${driverIdNum}`, {
          params: { 
              lat: parseFloat(lat), 
              lng: parseFloat(lng), 
              radius_km: parseFloat(radiusKm) 
          }
      })
  },
  
  // Accept a delivery
  acceptDelivery: (deliveryId, driverId) => {
      // Ensure IDs are numbers
      const deliveryIdNum = parseInt(deliveryId)
      const driverIdNum = parseInt(driverId)
      
      if (isNaN(deliveryIdNum)) {
          throw new Error(`Invalid delivery ID: ${deliveryId}`)
      }
      if (isNaN(driverIdNum)) {
          throw new Error(`Invalid driver ID: ${driverId}`)
      }
      
      return api.post(`/delivery/accept/${deliveryIdNum}`, { 
          driver_id: driverIdNum 
      })
  },
  
  // Get driver's active deliveries
  getActiveDeliveries: (driverId) => {
      const driverIdNum = parseInt(driverId)
      if (isNaN(driverIdNum)) {
          throw new Error(`Invalid driver ID: ${driverId}`)
      }
      return api.get(`/delivery/active/${driverIdNum}`)
  },
    
  // Update delivery status
  updateDeliveryStatus: (deliveryId, status) => {
      const deliveryIdNum = parseInt(deliveryId)
      if (isNaN(deliveryIdNum)) {
          throw new Error(`Invalid delivery ID: ${deliveryId}`)
      }
      return api.put(`/delivery/status/${deliveryIdNum}`, { status })
  },
  
  // Get delivery details
  getDeliveryDetails: (deliveryId) => {
      const deliveryIdNum = parseInt(deliveryId)
      if (isNaN(deliveryIdNum)) {
          throw new Error(`Invalid delivery ID: ${deliveryId}`)
      }
      return api.get(`/delivery/${deliveryIdNum}`)
  }
}

export const adminCompanyService = {
  // Get company details for admin
  getCompany: (companyId) =>
    api.get(`/admin/company/${companyId}`),

  getSetting: (key) => {
    return api.get(`/admin/get-setting`, {
      params: { key: key }
    });
  },

  // Get Agreement
  getAgreement: () =>
    api.get(`/admin/agreement`),

  getListTaf: () =>
    api.get(`/admin/select-list-taf`),
  
  // Accept Agreement
  acceptAgreement: () =>
    api.post(`/admin/agreement/accept`),
  
  // Update company details
  updateCompany: (companyId, companyData) =>
    api.put(`/admin/company/${companyId}`, companyData),

  updateUserRoles: (payload) => {
    return api.post('/admin/update-user-roles', {
      users: payload
    });
  },
  
  // Upload company logo
  uploadLogo: (companyId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/admin/company/${companyId}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  
  // Upload service image
  uploadServiceImage: (companyId, serviceIndex, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/admin/company/${companyId}/upload-service-image/${serviceIndex}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  getPermissionMetadata() {
    return api.get('/admin/permissions/metadata');  // returns { namespaces, labels }
  },
  getCompanyPermissions(companyId) {
    return api.get(`/admin/companies/${companyId}/permissions`); // { ui_state }
  },
  updateCompanyPermissions(companyId, uiState) {
    return api.patch(`/admin/companies/${companyId}/permissions`, uiState);
  },
  getAllCompanies() {
    return api.get('/admin/companies'); // you already implemented this
  },

  checkAdminOfAdmin(){
    return api.get('/admin/check-admin-of-admin');
  },

  getFunctionUpdateStatus: () =>
    api.get('/admin/companies/functions-update-status'),

  syncCompanyFunctions: (companyId, customTimeout = 300000) =>
    api.post(`/admin/companies/${companyId}/sync-functions`, null, {
      timeout: customTimeout,
    }),
}

export const officeService = {
  // Get activities for a specific Taf (user)
  getSelectActTaf: (idTaf) => {
    return api.get(`/office/select-act-taf`, {
      params: { id_taf: idTaf }
    });
  },

  // Get ATF (documents) for a specific Taf and Activity
  getSelectAtfTaf: (idTaf, act) => {
    return api.get(`/office/select-atf-taf`, {
      params: { 
        id_taf: idTaf,
        act: act
      }
    });
  },

  // Get ATF using POST method
  getSelectAtfTafPost: (idTaf, act) => {
    return api.post(`/office/select-atf-taf`, { 
      id_taf: idTaf,
      act: act
    });
  },

  saveDocument: (data, file = null) => {
    const formData = new FormData();
    // Always send as JSON string in form data
    formData.append('request', JSON.stringify(data));
    if (file) {
      formData.append('file', file);
    }
    
    return api.post('/office/save-doc', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  selectDocItem: (docIdItem) => {
    return api.get('/office/select-doc-item', {
      params: { doc_id_item: docIdItem }
    });
  },

  listDocAct: (docIdItem) => {
    return api.get('/office/list-doc-act', {
      params: { doc_code: docIdItem }
    });
  },

  listDocs: (subcodeId, ac = 0, act = 0) => {
    return api.get(`/office/list-docs`, {
      params: {
        subcode_id: subcodeId,
        ac,           // 0 = no filter
        act,          // 0 = no filter
      },
    });
  },

  listDoc2: (subcodeId, ac = 0, act = 0) => {
    return api.get(`/office/list-docs`, {
      params: {
        subcode_id: subcodeId,
        ac,
        act,
      }
    });
  },

  searchParents: (search = '') => {
    return api.get('/office/search-parents', {
      params: search ? { search: search } : {}
    });
  },

  searchParentsAll: (search = '') => {
    return api.get('/office/search-parents-all', {
      params: { search }
    });
  },

  referenceDocument: (data, file = null) => {
    const formData = new FormData();
    // Always send as JSON string in form data
    formData.append('request', JSON.stringify(data));
    if (file) {
      formData.append('file', file);
    }
    
    return api.post('/office/reference-doc', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  closeOrRateDoc(payload) {
    return api.post('/office/close-rate-doc', payload)
  },

  listActDocs() {
    return api.get('/office/act-docs')
  },

  saveSeenDoc(payload) {
      // payload = { Sub, typ, id_Doc }
      return api.post('/office/save-seen-doc', payload)
  },

  getAccountTitles(subCodes) {
      // subCodes can be an array [1,2,3] or a comma-separated string ",1,2,3,"
      return api.post('/office/account-titles', { SubCodes: subCodes })
  },
    // ---------- PUSH NOTIFICATIONS ----------
  getVapidPublicKey() {
    return api.get('/office/push/vapid-public-key')
  },

  subscribePush(subscription) {
    const json = subscription.toJSON ? subscription.toJSON() : subscription
    const payload = {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
    }
    return api.post('/office/push/subscribe', payload)
  },

  getPushSubscriptionStatus(subscription) {
    const json = subscription.toJSON ? subscription.toJSON() : subscription
    return api.post('/office/push/status', {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
    })
  },

  unsubscribePush(subscription) {
    const json = subscription.toJSON ? subscription.toJSON() : subscription
    return api.post('/office/push/unsubscribe', {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
    })
  },

  getPersonDocuments: (personCode, dateFrom = null, dateTo = null) => {
    return api.get('/office/person-documents', {
      params: {
        person_code: personCode,
        date_from: dateFrom,
        date_to: dateTo
      }
    });
  },
  getTwoPersonDocuments: (personCode1, personCode2, dateFrom = null, dateTo = null) => {
    return api.get('/office/two-person-documents', {
      params: {
        person_code_1: personCode1,
        person_code_2: personCode2,
        date_from: dateFrom,
        date_to: dateTo,
      },
    });
  },
}

export const databaseService = {
  getCompanyDatabases: () =>
    api.get(`/company/databases`),

  switchDatabase: (targetDb) => {
    return api.post('/switch-database', {
      target_db: targetDb
    })
  }
}

export const invoiceService = {
  // Save invoice
  saveInvoice: (invoiceData) =>
    api.post('/sales/save-invoice', invoiceData),
  
  // Get invoice history
  getInvoiceHistory: (params) =>
    api.get('/sales/invoice-history', { params }),
  
  // Get invoice details
  getInvoiceDetails: (invoiceId) =>
    api.get(`/sales/invoice/${invoiceId}`),

  getInvoices: () =>
    api.get('/sales/invoices'),

  getInvoiceDetail: (invoiceId, ac, act) =>
    api.get(`/sales/invoice/${invoiceId}`, {
      params: { ac, act }
    }),
}

export const healthService = {
  check: () => api.get('/health')
}

export default api
